"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import UserService from "@/api/users.service";
import { Input } from "@/components/ui/input"
import { SendIcon, Loader2, ArrowLeft } from "lucide-react"
import {useToast} from "@/hooks/use-toast";
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import countries from "@/components/Countries"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

const formSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: z.string().regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
    message: "Please enter a valid email address"
  }),
  phoneNumber: z.string().optional(),
  gender: z.string().optional(),
  city: z.string().optional(),
  physicalAddress: z.string().optional(),
  country: z.string().optional(),
  isAdmin: z.boolean(),
  role: z.string(),
})

const inviteFormSchema = z.object({
  emailAddress: z.string().regex(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
    message: "Please enter a valid email address"
  }),
  role: z.string(),
})

export type User = {
  id: number
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  gender: string
  city: string
  physicalAddress: string
  country: string
  isAdmin: boolean
  role: string
}
export default function CreateUserForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  
  // Form for new user
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "", 
      emailAddress: "",
      phoneNumber: "",
      gender: "",
      city: "",
      physicalAddress: "",
      country: "",
      isAdmin: false,
      role: "",
    },
  })

  // Form for invite
  const inviteForm = useForm<z.infer<typeof inviteFormSchema>>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      emailAddress: "",
      role: "",
    },
  })

  const { toast } = useToast();

  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      const response = await UserService.addUser(selectedBusiness.id, { ...values, isNewUser });
      const result = response.data;
      if (result.result === "success") {
        showToastWithTimeout("success", "✅ Success", "User added successfully", 3000);
        form.reset();
      } else {
        showToastWithTimeout("error", "❌ Error", result.message || "Failed to add user", 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Error", "Failed to add user", 3000);
    } finally {
      setIsSubmitting(false);
    }
  }

  const onInvite = async (values: z.infer<typeof inviteFormSchema>) => {
    setIsSubmitting(true);
    try {
      const response = await UserService.addUser(selectedBusiness.id, { ...values, isNewUser: false });
      const result = response.data;
      if (result.result === "success") {
        showToastWithTimeout("success", "✅ Success", "User invited successfully", 3000);
        inviteForm.reset();
      } else {
        showToastWithTimeout("error", "❌ Error", result.message || "Failed to invite user", 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Error", "Failed to invite user", 3000);
    } finally {
      setIsSubmitting(false);
    }
  }

  console.log("countries", countries);

  return (
    <div className="container py-10 mx-auto bg-white px-10 rounded-lg">
      <h2 className="text-3xl font-bold tracking-tight">Add New User</h2>
      <div className='flex items-start align-middle mb-5'>
        <Button onClick={() => window.history.back()} className='mr-2'> 
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>

      <Tabs 
        defaultValue="new" 
        className="w-full"
        onValueChange={(value) => setIsNewUser(value === "new")}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new">Create New User</TabsTrigger>
          <TabsTrigger value="invite">Invite User</TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="First Name" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the first name.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Last Name" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the last name.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="emailAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the email address.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <PhoneInput placeholder="+1234567890" {...field} className="appearance-none px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
                      </FormControl>
                      <FormDescription>
                        Enter the phone number.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <FormControl>
                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Select the gender.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="physicalAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Physical Address</FormLabel>
                      <FormControl>
                        <Input placeholder="123 Main St" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the physical address.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="City" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the city.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Select {...field} defaultValue={field.value ?? ''} value={field.value ?? ''} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a country" />
                          </SelectTrigger>
                          <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll', color: 'black' }}>
                            {countries.map((country: any) => (
                              <SelectItem key={country} value={country}>
                                {country}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Enter the country.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isAdmin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Admin <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Select onValueChange={(value) => {
                          field.onChange(value === "true");
                          if (value === "true") {
                            form.setValue("role", "Admin");
                          }
                        }} defaultValue={field.value ? "true" : "false"}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select admin status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Select if the user is an admin.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="accountant">Accountant</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                            <SelectItem value="marketing">Marketing</SelectItem>
                            <SelectItem value="customer-support">Customer Support</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Select the role.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" className="bg-[#0E415E] text-white">
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</> : <><SendIcon className="w-4 h-4 mr-2" /> Create User</>}
                </Button>
              </div>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="invite">
          <Form {...inviteForm}>
            <form onSubmit={inviteForm.handleSubmit(onInvite)} className="space-y-8">
              <div className="grid sm:grid-cols-2 grid-cols-1 gap-4">
                <FormField
                  control={inviteForm.control}
                  name="emailAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Enter the email address of the user to invite.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={inviteForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role <span className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent style={{ maxHeight: '200px', overflowY: 'scroll' }}>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="accountant">Accountant</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        Select the role for the invited user.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" className="bg-[#0E415E] text-white">
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Inviting...</> : <><SendIcon className="w-4 h-4 mr-2" /> Invite User</>}
                </Button>
              </div>
            </form>
          </Form>
        </TabsContent>
      </Tabs>
    </div>
  )
}
